import $ from "jquery";
import { PMTiles } from "pmtiles";
import { MapView } from "./Basemap";
import { ForecastTime, InitialTime, ShadeSelector } from "./Controller";
import { WIND_PMTILES_CUTOFF, beforeInitialCutoff } from "./DataAvailability";
import {
    ColorPicker,
    InputNumber,
    Selectbox,
    setFeatureAvailability,
} from "./Form";
import { gslForecastKey, gslTargetKey, usesGslAnalysis } from "./GslTime";
import { getMapMeta } from "./MapMeta";
import { readColor, readFlag } from "./UrlState";
import { WindGeometry, decodeWindGeometry } from "./WindGeometry";
import { dataPath } from "./paths";

interface TileGeometry {
    geometry: WindGeometry | null;
    x: number;
    y: number;
}

type WindVersion =
    | "srfN"
    | "srfS"
    | "srfF"
    | "975"
    | "925"
    | "850"
    | "500"
    | "300";

const SVG_NS = "http://www.w3.org/2000/svg";
const DEFAULT_COLORS = [
    "#ffffff4d", "#ffffff99", "#ffffffff", "#ffffffff", "#ffff33ff",
    "#ffff33ff", "#ff9900ff", "#ff0000ff", "#991133ff", "#660000ff", "#660000ff",
]; // biome-ignore format:
const THREE_COLORS = [
    "#bbddbbff", "#bbddbbff", "#bbddbbff", "#bbddbbff", "#ffdd00ff",
    "#ffdd00ff", "#ffdd00ff", "#ee0033ff", "#ee0033ff", "#ee0033ff", "#ee0033ff",
]; // biome-ignore format:
const LEVEL_RANGE: Record<string, [number, number]> = {
    LFM: [5, 7],
    MSM: [4, 6],
    QMA: [4, 6],
    GSM: [3, 5],
    GS5: [3, 5],
    GSA: [3, 5],
    GSL: [3, 5],
};

export class Wind {
    private readonly svgGroup = document.querySelector<SVGGElement>("#wind")!;
    private readonly adjustment = new Selectbox("#select_Wlev");
    private readonly version = new Selectbox("#select_Wver");
    private readonly colorMode = new Selectbox("#select_Wcol");
    private readonly minimum = new Selectbox("#select_Wmin");
    private readonly strokeWidth = new InputNumber("#input_Wstw", { value: 1 });
    private readonly markerSize = new InputNumber("#input_Wmsz", {
        value: 1.5,
    });
    private readonly weakMarkerSize = new InputNumber("#input_Wmsz0", {
        value: 0.9,
    });
    private readonly singleColor = new ColorPicker(
        "#colpic_wind",
        "#ffffffff",
        ["#333333", "#6699dd", "#ffffff", "#eeff66"],
    );
    private readonly classColors = DEFAULT_COLORS.map(
        (color, index) =>
            new ColorPicker(`#colpic_wind${index}`, color, DEFAULT_COLORS),
    );
    private readonly archives = new Map<string, Promise<PMTiles>>();
    private readonly archiveOrder: string[] = [];
    private prefetchPreviousFt: import("decimal.js").default | null = null;
    private readonly geometries = new Map<
        string,
        Promise<WindGeometry | null>
    >();
    private readonly geometryOrder: string[] = [];
    private readonly legacySvgs = new Map<string, Promise<Document | null>>();
    private readonly legacySvgOrder: string[] = [];
    // 風凡例 (旧版 legendW の移植。旧 type T/H/V はすべて縦型、N は非表示)
    private readonly legend = document.createElementNS(SVG_NS, "g");
    private legendX = 50;
    private legendY = 350;
    private legendType: "V" | "N" = "V";
    private changeCallback: (task: Promise<unknown>) => void = () => {};
    private sequence = 0;
    private enabled = false;

    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
        private readonly initialTime: InitialTime,
        private readonly forecastTime: ForecastTime,
        private readonly mapView: MapView,
        private readonly getImageLevel: () => number,
    ) {
        this.svgGroup.style.pointerEvents = "none";

        $("#btn_wind_on").on("click", () => this.setEnabled(true));
        $("#btn_wind_off").on("click", () => this.setEnabled(false));
        for (const control of [
            this.adjustment,
            this.version,
            this.colorMode,
            this.minimum,
        ]) {
            control.onChange(() => this.changed());
        }
        for (const control of [
            this.strokeWidth,
            this.markerSize,
            this.weakMarkerSize,
        ]) {
            control.onChange(() => this.changed());
        }
        this.singleColor.onChange(() => this.changed());
        this.classColors.forEach((picker) =>
            picker.onChange(() => this.changed()),
        );
        $("#wind_preset_normal").on("click", () =>
            this.usePreset(DEFAULT_COLORS),
        );
        $("#wind_preset_three").on("click", () => this.usePreset(THREE_COLORS));
        this.legend.id = "legendW";
        this.legend.style.pointerEvents = "auto";
        this.legend.style.cursor = "move";
        document.querySelector("#overlay")!.appendChild(this.legend);
        this.installLegendDrag();
        this.loadParams();
        this.updateModelOptions();
        this.updateThresholds();
        this.updateUi();
    }

    onChange(callback: (task: Promise<unknown>) => void) {
        this.changeCallback = callback;
    }
    saveParams() {
        this.params.set("wd_on", this.enabled ? "1" : "0");
        this.params.set("wd_adj", this.adjustment.value);
        this.params.set("wd_ver", this.version.value);
        this.params.set("wd_stw", String(this.strokeWidth.value));
        this.params.set("wd_msz", String(this.markerSize.value));
        this.params.set("wd_msz0", String(this.weakMarkerSize.value));
        this.params.set("wd_min", this.minimum.value);
        this.params.set("wd_col", this.colorMode.value);
        this.params.set("wd_color", this.singleColor.getColor());
        this.params.set(
            "wd_colors",
            this.classColors.map((picker) => picker.getColor()).join(","),
        );
        this.params.set("lgdW_x", String(Math.round(this.legendX)));
        this.params.set("lgdW_y", String(Math.round(this.legendY)));
        this.params.set("lgdW_type", this.legendType);
    }
    loadParams() {
        this.enabled = readFlag(this.params, "wd_on", "0") === "1";
        this.adjustment.value = this.validChoice(
            "wd_adj",
            ["-1", "0", "1"],
            "0",
        );
        this.version.value = this.validChoice(
            "wd_ver",
            ["srfN", "srfS", "srfF", "975", "925", "850", "500"],
            "srfN",
        );
        this.colorMode.value = this.validChoice("wd_col", ["Z", "D", "M"], "Z");
        this.minimum.value = this.validChoice(
            "wd_min",
            [...Array(11)].map((_, index) => String(index)),
            "0",
        );
        this.strokeWidth.value = this.numberParam("wd_stw", 1, 0, 20);
        this.markerSize.value = this.numberParam("wd_msz", 1.5, 0, 20);
        this.weakMarkerSize.value = this.numberParam("wd_msz0", 0.9, 0, 20);
        this.singleColor.setColor(
            readColor(this.params, "wd_color", "#ffffffff"),
        );
        const colors = this.params.get("wd_colors")?.split(",");
        if (
            colors?.length === 11 &&
            colors.every((color) => /^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(color))
        ) {
            colors.forEach((color, index) =>
                this.classColors[index].setColor(color),
            );
        } else {
            DEFAULT_COLORS.forEach((color, index) =>
                this.classColors[index].setColor(color),
            );
        }
        this.legendX = this.numberParam("lgdW_x", 50, -1000, 3000);
        this.legendY = this.numberParam("lgdW_y", 350, -1000, 2000);
        this.legendType = this.params.get("lgdW_type") === "N" ? "N" : "V";
        this.updateUi();
    }
    onShadeVariableChanged() {
        this.updateModelOptions();
        this.updateAvailability();
        return this.update();
    }
    onInitialTimeChanged() {
        return this.update();
    }
    onForecastTimeChanged() {
        return this.update();
    }
    onViewChanged() {
        return this.update();
    }
    async toCaptureMode(_width: number) {
        // PMTiles geometry is表示中SVGへ直接描画済みなので再描画不要。
    }
    async toDisplayMode() {
        // capture専用状態を持たない。
    }
    async update() {
        const currentSequence = ++this.sequence;
        if (!this.enabled) {
            this.clear();
            return;
        }
        if (!this.supportsCurrentModel()) {
            this.enabled = false;
            this.updateUi();
            this.clear();
            this.setStatus("このモデルには風ベクトルがありません。");
            return;
        }
        if (!(await this.hasCurrentInitial())) {
            this.enabled = false;
            this.updateUi();
            this.clear();
            this.setStatus("この初期値の風データは作成中です。");
            return;
        }
        this.setStatus("");
        const level = this.effectiveLevel();
        if (this.usesLegacyData()) {
            await this.loadLegacy(currentSequence, level);
            return;
        }
        const z = level - 3;
        const tileCount = 2 ** z;
        const { model } = this.shadeSelector.getParams();
        const meta = getMapMeta(model);
        const tileWidth = meta.width / tileCount;
        const tileHeight = meta.height / tileCount;
        const sourceView = this.mapView.getViewBox();
        // 回転時は「回転後の表示矩形」を覆う軸平行境界箱 (中心回りの回転なので
        // 半幅 = |w/2·cos|+|h/2·sin| で任意角を正確に覆える)。固定1タイル余白では
        // 大角度×広域で四隅が欠ける。
        let view = {
            x: sourceView.x,
            y: sourceView.y,
            width: sourceView.width,
            height: sourceView.height,
        };
        if (sourceView.deg !== 0) {
            const radians = (sourceView.deg * Math.PI) / 180;
            const cos = Math.abs(Math.cos(radians));
            const sin = Math.abs(Math.sin(radians));
            const halfWidth =
                (sourceView.width / 2) * cos + (sourceView.height / 2) * sin;
            const halfHeight =
                (sourceView.width / 2) * sin + (sourceView.height / 2) * cos;
            const centerX = sourceView.x + sourceView.width / 2;
            const centerY = sourceView.y + sourceView.height / 2;
            view = {
                x: centerX - halfWidth,
                y: centerY - halfHeight,
                width: halfWidth * 2,
                height: halfHeight * 2,
            };
        }
        const x0 = Math.max(0, Math.floor((view.x - meta.x0) / tileWidth));
        const x1 = Math.min(
            tileCount - 1,
            Math.floor((view.x + view.width - meta.x0) / tileWidth),
        );
        const y0 = Math.max(0, Math.floor((view.y - meta.y0) / tileHeight));
        const y1 = Math.min(
            tileCount - 1,
            Math.floor((view.y + view.height - meta.y0) / tileHeight),
        );
        const url = this.archiveUrl();
        const jobs: Promise<TileGeometry>[] = [];
        for (let y = y0; y <= y1; y++) {
            for (let x = x0; x <= x1; x++) {
                jobs.push(
                    this.getGeometry(url, z, x, y).then((geometry) => ({
                        geometry,
                        x,
                        y,
                    })),
                );
            }
        }
        let tiles: TileGeometry[];
        try {
            tiles = await Promise.all(jobs);
        } catch (error) {
            if (currentSequence === this.sequence) {
                if (model === "GSA" || model === "GSL") {
                    this.clear();
                    throw error;
                }
                // カットオーバー境界の歯抜けにも対応する。旧SVGが無ければ
                // loadLegacy側で元の取得失敗として通知する。
                await this.loadLegacy(currentSequence, level);
            }
            return;
        }
        if (currentSequence !== this.sequence) {
            return;
        }
        this.render(tiles, view, level);
        this.drawLegend();
        this.prefetch(z, x0, x1, y0, y1);
    }

    private usesLegacyData() {
        const { model } = this.shadeSelector.getParams();
        const { ini } = this.initialTime.getParams();
        const cutoff = WIND_PMTILES_CUTOFF[model];
        if (!cutoff) {
            return false;
        }
        if (model !== "QMA") {
            return beforeInitialCutoff(ini, cutoff);
        }
        const { ft } = this.forecastTime.getParams();
        return beforeInitialCutoff(
            ini.add(ft.ceil().toNumber(), "hour"),
            cutoff,
        );
    }

    private legacyUrl(level: number) {
        const { model } = this.shadeSelector.getParams();
        const { ini } = this.initialTime.getParams();
        const { ft } = this.forecastTime.getParams();
        if (model === "QMA") {
            const integer = ft.floor();
            const decimal = ft.sub(integer).mul(10).round().toNumber();
            const directory = ini.add(
                integer.toNumber() + Math.ceil(decimal / 10),
                "hour",
            );
            const target = ini.add(integer.toNumber(), "hour");
            return dataPath(
                `QMAW/${directory.format("YYYYMM")}/${directory.format("YYYYMMDD-HH")}/QMA-W${this.version.value}-Lv${level}_${target.format("YYYYMMDD-HH")}.${decimal}.svg`,
            );
        }
        const initial = ini.format("YYYYMMDD-HH");
        const forecast = ft
            .toFixed(1)
            .padStart(model === "GS5" || model === "GSA" ? 5 : 4, "0");
        return dataPath(
            `${model}W/${ini.format("YYYYMM")}/${initial}/${model}-W${this.version.value}-Lv${level}_${initial}_FT${forecast}.svg`,
        );
    }

    private getLegacySvg(url: string) {
        const cached = this.legacySvgs.get(url);
        if (cached) {
            return cached;
        }
        const promise = fetch(url).then(async (response) => {
            if (!response.ok) {
                return null;
            }
            const document = new DOMParser().parseFromString(
                await response.text(),
                "image/svg+xml",
            );
            return document.querySelector("parsererror") ? null : document;
        });
        promise.catch(() => this.legacySvgs.delete(url));
        this.legacySvgs.set(url, promise);
        this.legacySvgOrder.push(url);
        while (this.legacySvgOrder.length > 12) {
            this.legacySvgs.delete(this.legacySvgOrder.shift()!);
        }
        return promise;
    }

    private async loadLegacy(currentSequence: number, requestedLevel: number) {
        const { model } = this.shadeSelector.getParams();
        const minimum = LEVEL_RANGE[model][0];
        let source: Document | null = null;
        let level = requestedLevel;
        let url = "";
        // 過去データは容量削減で最上位Lvが落ちている。GPV-1.0と同じく
        // 存在する最も細かいSVGを使う。
        for (; level >= minimum; level--) {
            url = this.legacyUrl(level);
            source = await this.getLegacySvg(url);
            if (source || currentSequence !== this.sequence) {
                break;
            }
        }
        if (currentSequence !== this.sequence) {
            return;
        }
        const sourceGroup = source?.querySelector("svg > g");
        if (!sourceGroup) {
            this.clear();
            this.setStatus("風データを取得できませんでした。");
            throw new Error(`wind SVG not found: ${url}`);
        }
        const group = document.importNode(sourceGroup, true) as SVGGElement;
        group.removeAttribute("display");
        group.dataset.format = "legacy-svg";
        group.dataset.sourceLevel = String(level);
        const defs = document.createElementNS(SVG_NS, "defs");
        const styles = this.styles();
        const prefixes = ["C", "F", "W"];
        for (let direction = 0; direction < prefixes.length; direction++) {
            for (let speed = 0; speed < 11; speed++) {
                const className = `${prefixes[direction]}${speed}`;
                const style = styles[direction][speed];
                const size =
                    this.markerSize.value +
                    ((speed - 5) / 5) *
                        (this.markerSize.value - this.weakMarkerSize.value);
                const markerId = `legacy_wind_marker_${className}`;
                const marker = svgElement("marker", {
                    id: markerId,
                    markerUnits: "strokeWidth",
                    markerWidth: size * 10,
                    markerHeight: size * 10,
                    viewBox: "0 0 10 10",
                    refX: 5,
                    refY: 5,
                    orient: "auto",
                });
                marker.appendChild(
                    svgElement("polygon", {
                        points: "0,0 5,5 0,10 10,5",
                        fill: style.color,
                        opacity: style.alpha,
                    }),
                );
                defs.appendChild(marker);
                for (const node of group.querySelectorAll<SVGElement>(
                    `.${className}`,
                )) {
                    node.setAttribute("fill", "none");
                    node.setAttribute("stroke", style.color);
                    node.setAttribute("stroke-opacity", String(style.alpha));
                    node.setAttribute(
                        "display",
                        speed >= Number(this.minimum.value) ? "" : "none",
                    );
                    for (const path of node.querySelectorAll("path")) {
                        path.setAttribute("marker-end", `url(#${markerId})`);
                    }
                }
            }
        }
        group.setAttribute("fill", "none");
        group.setAttribute("stroke-width", String(this.lineWidth(level)));
        this.svgGroup.replaceChildren(defs, group);
        this.drawLegend();
        this.setStatus("");
    }

    // 旧版 legendW (GPV-1.0〜1.6 mydraw.js) の移植。段彩(Z)/風向(D)/単色(M) 対応
    private drawLegend() {
        this.legend.textContent = "";
        if (!this.enabled || this.legendType === "N") {
            return;
        }
        this.legend.setAttribute(
            "transform",
            `translate(${this.legendX} ${this.legendY}) rotate(0.005)`,
        );
        const { values, unit, labels, surface } = this.thresholdParams();
        const mode = this.colorMode.value;
        const m = 13;
        const f = 35;
        const cx = 50;
        const cy = 50;
        const w = mode === "D" && surface ? 190 : 150;
        const th = surface ? 60 : 95;
        const bg = svgElement("rect", {
            width: w,
            height: 0,
            fill: "#cfc",
            rx: 12,
        });
        this.legend.appendChild(bg);
        if (surface) {
            this.legend.appendChild(
                svgText(
                    w / 2,
                    m + 25 + 50 * 0.35,
                    mode === "D" ? "風向" : "風速",
                    50,
                ),
            );
        } else {
            const title = svgText(w / 2, m + 25 + 50 * 0.35, "", 50);
            const small = document.createElementNS(SVG_NS, "tspan");
            small.setAttribute("font-size", "30");
            small.textContent = "上空の";
            const large = document.createElementNS(SVG_NS, "tspan");
            large.textContent = "風";
            title.append(small, large);
            this.legend.appendChild(title);
            const altitudes: Record<string, string> = {
                "975": "約300m",
                "925": "約750m",
                "850": "約1500m",
                "500": "約5500m",
            };
            this.legend.appendChild(
                svgText(
                    w / 2,
                    m + 70 + 30 * 0.35,
                    altitudes[this.version.value] ?? "",
                    30,
                ),
            );
        }
        if (mode === "Z") {
            const styles = this.classColors.map((picker) =>
                this.colorStyle(picker.getColor()),
            );
            // 目盛り間隔の最大値 (セル高さの基準)
            let previous = 0;
            let maxSpan = 0;
            for (let i = 1; i < 11; i++) {
                if (labels[i] > 0) {
                    maxSpan = Math.max(maxSpan, values[i] - values[previous]);
                    previous = i;
                }
            }
            const minimum = Number(this.minimum.value);
            let rows = 0;
            for (let i = 9; i >= minimum; i--) {
                if (labels[i] < 0) {
                    continue;
                }
                const ratio = (values[i + 1] - values[i]) / maxSpan;
                this.legend.appendChild(
                    svgElement("rect", {
                        x: m,
                        y: m + th + rows * cy,
                        width: cx,
                        height: cy * ratio,
                        fill: styles[i].color,
                        opacity: styles[i].alpha,
                    }),
                );
                rows += ratio;
                if (labels[i] > 0) {
                    this.legend.appendChild(
                        svgText(
                            2 * m + cx + (f * 3) / 4,
                            m + th + rows * cy + f * 0.35,
                            String(values[i]),
                            f,
                        ),
                    );
                    if (i === minimum) {
                        rows++; // 単位の領域確保
                    }
                }
            }
            rows = Math.ceil(rows);
            this.legend.appendChild(
                svgText(w - m, th + rows * cy + 30 * 0.35, unit, 30, "end"),
            );
            bg.setAttribute("height", String(2 * m + th + rows * cy));
        } else if (mode === "D") {
            const colors = ["#33f", "#ff6", "#f33"];
            const texts = surface
                ? ["北寄り", "東・西", "南寄り"]
                : ["寒", "", "暖"];
            const adjustX = surface ? (f * 5.5) / 4 : (f * 3) / 4;
            for (let i = 0; i < 3; i++) {
                this.legend.appendChild(
                    svgElement("rect", {
                        x: m,
                        y: m + th + i * cy,
                        width: cx,
                        height: cy,
                        fill: colors[i],
                    }),
                );
                this.legend.appendChild(
                    svgText(
                        2 * m + cx + adjustX,
                        m + th + (i + 0.5) * cy + f * 0.35,
                        texts[i],
                        f,
                    ),
                );
            }
            bg.setAttribute("height", String(2 * m + th + 3 * cy));
        } else {
            bg.setAttribute("height", String(m + th));
        }
    }

    private installLegendDrag() {
        const overlay = document.querySelector<SVGSVGElement>("#overlay")!;
        let offsetX = 0;
        let offsetY = 0;
        const point = (event: PointerEvent) => {
            const svgPoint = overlay.createSVGPoint();
            svgPoint.x = event.clientX;
            svgPoint.y = event.clientY;
            return svgPoint.matrixTransform(overlay.getScreenCTM()!.inverse());
        };
        this.legend.addEventListener("pointerdown", (event) => {
            const p = point(event);
            offsetX = p.x - this.legendX;
            offsetY = p.y - this.legendY;
            this.legend.setPointerCapture(event.pointerId);
        });
        this.legend.addEventListener("pointermove", (event) => {
            if (!this.legend.hasPointerCapture(event.pointerId)) {
                return;
            }
            const p = point(event);
            this.legendX = p.x - offsetX;
            this.legendY = p.y - offsetY;
            this.legend.setAttribute(
                "transform",
                `translate(${this.legendX} ${this.legendY}) rotate(0.005)`,
            );
        });
    }

    // 近傍 (前方12・後方6) の風 pmtiles を表示中のタイル範囲で先読みする。
    // 進行方向側を先に取得する (画像系と同じ規則。2026-08-02)
    private prefetch(
        z: number,
        x0: number,
        x1: number,
        y0: number,
        y1: number,
    ) {
        const { ft } = this.forecastTime.getParams();
        const direction =
            this.prefetchPreviousFt === null ||
            ft.gte(this.prefetchPreviousFt)
                ? (1 as const)
                : (-1 as const);
        this.prefetchPreviousFt = ft;
        for (const neighbor of this.forecastTime.getNeighborFts(
            ft,
            direction,
        )) {
            const url = this.archiveUrl(neighbor);
            for (let y = y0; y <= y1; y++) {
                for (let x = x0; x <= x1; x++) {
                    this.getGeometry(url, z, x, y).catch(() => undefined);
                }
            }
        }
    }

    private setEnabled(enabled: boolean) {
        this.enabled = enabled;
        this.updateUi();
        this.changeCallback(this.update());
    }
    private changed() {
        this.updateThresholds();
        this.changeCallback(this.update());
    }
    private usePreset(colors: string[]) {
        this.colorMode.value = "Z";
        colors.forEach((color, index) =>
            this.classColors[index].setColor(color),
        );
        this.changed();
    }
    private updateUi() {
        this.updateAvailability();
        $("#btn_wind_on")
            .toggleClass("btn_onA", this.enabled)
            .toggleClass("btn_onB", !this.enabled);
        $("#btn_wind_off")
            .toggleClass("btn_offB", this.enabled)
            .toggleClass("btn_offA", !this.enabled);
        $("#sdm_wind_detail").css("display", this.enabled ? "block" : "none");
    }
    private updateModelOptions() {
        const model = this.shadeSelector.getParams().model;
        const allowed: Record<string, WindVersion[]> = {
            GSM: ["srfN", "srfS", "srfF", "925", "850", "500"],
            GS5: ["srfN", "srfS", "srfF"],
            GSA: ["srfN", "srfS", "srfF"],
            GSL: ["srfN", "850", "500", "300"],
            MSM: ["srfN", "srfS", "srfF", "975", "925", "850"],
            LFM: ["srfN", "srfS", "srfF", "975", "925", "850"],
            QMA: ["srfN", "srfS", "srfF", "975", "925", "850"],
        };
        const choices = allowed[model] || [];
        document
            .querySelectorAll<HTMLOptionElement>("#select_Wver option")
            .forEach(
                (option) =>
                    (option.disabled = !choices.includes(
                        option.value as WindVersion,
                    )),
            );
        if (!choices.includes(this.version.value as WindVersion)) {
            this.version.value = choices[0] || "srfN";
        }
        this.updateThresholds();
    }
    // 凡例と閾値UIで共用する版別パラメータ (labels: 1=凡例に数字表記, -1=凡例に出さない)
    private thresholdParams() {
        const upper = ["500", "300"].includes(this.version.value);
        const surface = this.version.value.startsWith("srf");
        const values = surface
            ? [0, 2, 4, 6, 8, 10, 12, 16, 20, 24, 28]
            : upper
              ? [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
              : [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
        const unit = surface ? "m/s" : "kt";
        const labels = surface
            ? [0, 0, 1, 0, 1, 0, 1, 1, 1, 1, -1]
            : [0, 0, 1, 0, 1, 0, 1, 0, 1, 0, -1];
        return { values, unit, labels, surface };
    }
    private updateThresholds() {
        const { values, unit } = this.thresholdParams();
        values.forEach((value, index) => $(`#wd_t${index}`).text(value));
        $("#wd_t6-2").text(values[6]);
        $("#wd_tu").text(unit);
        document
            .querySelectorAll<HTMLOptionElement>("#select_Wmin option")
            .forEach(
                (option, index) =>
                    (option.textContent = `${values[index]}${unit}`),
            );
    }
    private supportsCurrentModel() {
        return this.shadeSelector.getParams().model in LEVEL_RANGE;
    }
    private updateAvailability() {
        setFeatureAvailability("wind", this.supportsCurrentModel());
    }
    private async hasCurrentInitial() {
        const { model } = this.shadeSelector.getParams();
        const { ini } = this.initialTime.getParams();
        return this.initialTime.hasLatest(`${model}W`, ini);
    }
    private setStatus(message: string) {
        $("#wind_status").text(message);
    }
    private effectiveLevel() {
        const model = this.shadeSelector.getParams().model;
        const range = LEVEL_RANGE[model];
        const requested =
            this.getImageLevel() + 3 + Number(this.adjustment.value);
        return Math.max(range[0], Math.min(range[1], requested));
    }
    private archiveUrl(ft = this.forecastTime.getParams().ft) {
        const { model } = this.shadeSelector.getParams();
        const { ini } = this.initialTime.getParams();
        if (model === "GSL") {
            const version = this.gslFileVersion();
            if (usesGslAnalysis(ft)) {
                const { target, key } = gslTargetKey(ini, ft);
                return dataPath(
                    `GSLW/${target.format("YYYYMM")}/analysis/GSL-W${version}-A_${key}.pmtiles`,
                );
            }
            const initial = ini.format("YYYYMMDD-HH");
            return dataPath(
                `GSLW/${ini.format("YYYYMM")}/${initial}/GSL-W${version}_${initial}_FT${gslForecastKey(ft)}.pmtiles`,
            );
        }
        if (model === "QMA") {
            const integer = ft.floor();
            const decimal = ft.sub(integer).mul(10).round().toNumber();
            const directory = ini.add(
                integer.toNumber() + Math.ceil(decimal / 10),
                "hour",
            );
            const target = ini.add(integer.toNumber(), "hour");
            return dataPath(
                `QMAW/${directory.format("YYYYMM")}/${directory.format("YYYYMMDD-HH")}/QMA-W${this.version.value}_${target.format("YYYYMMDD-HH")}.${decimal}.pmtiles`,
            );
        }
        const initial = ini.format("YYYYMMDD-HH");
        const forecast = ft
            .toFixed(1)
            .padStart(model === "GS5" || model === "GSA" ? 5 : 4, "0");
        return dataPath(
            `${model}W/${ini.format("YYYYMM")}/${initial}/${model}-W${this.version.value}_${initial}_FT${forecast}.pmtiles`,
        );
    }
    private getArchive(url: string) {
        let archive = this.archives.get(url);
        if (!archive) {
            archive = Promise.resolve().then(async () => {
                const result = new PMTiles(url);
                await result.getHeader();
                const model = this.shadeSelector.getParams().model;
                if (model === "GSL") {
                    const metadata = (await result.getMetadata()) as {
                        format?: string;
                        origin?: number[];
                        full_svg_units?: number[];
                    };
                    const meta = getMapMeta(model);
                    if (
                        metadata.format !== "gpv-warrow-v2" ||
                        metadata.origin?.[0] !== meta.x0 ||
                        metadata.origin?.[1] !== meta.y0 ||
                        metadata.full_svg_units?.[0] !== meta.width ||
                        metadata.full_svg_units?.[1] !== meta.height
                    ) {
                        throw new Error("GSL wind metadata range mismatch");
                    }
                }
                return result;
            });
            archive.catch(() => this.archives.delete(url));
            this.archives.set(url, archive);
            this.archiveOrder.push(url);
            // タイムバー全体 (最大89本) が収まるだけ保持 (2026-08-02 拡大)
            while (this.archiveOrder.length > 128) {
                this.archives.delete(this.archiveOrder.shift()!);
            }
        }
        return archive;
    }
    private gslFileVersion() {
        return this.version.value === "srfN" ? "srf" : this.version.value;
    }
    private getGeometry(url: string, z: number, x: number, y: number) {
        const key = `${url}|${z}|${x}|${y}`;
        let geometry = this.geometries.get(key);
        if (!geometry) {
            geometry = this.getArchive(url).then(async (archive) => {
                const tile = await archive.getZxy(z, x, y);
                return tile ? decodeWindGeometry(tile.data) : null;
            });
            geometry.catch(() => this.geometries.delete(key));
            this.geometries.set(key, geometry);
            this.geometryOrder.push(key);
            while (this.geometryOrder.length > 4096) {
                this.geometries.delete(this.geometryOrder.shift()!);
            }
        }
        return geometry;
    }
    private render(
        tiles: TileGeometry[],
        view: { x: number; y: number; width: number; height: number },
        level: number,
    ) {
        void view;
        const lineWidth = this.lineWidth(level);
        const styles = this.styles();
        const marker = this.markerSize.value;
        const weakMarker = this.weakMarkerSize.value;
        const markerScale = [...Array(11)].map(
            (_, index) =>
                (marker + ((index - 5) / 5) * (marker - weakMarker)) *
                lineWidth,
        );
        const minimum = Number(this.minimum.value);
        const seen = new Set<string>();
        const tileCount = 2 ** (level - 3);
        const meta = getMapMeta(this.shadeSelector.getParams().model);
        const shafts = [...Array(33)].map(() => [] as string[]);
        const heads = [...Array(33)].map(() => [] as string[]);
        for (const tile of tiles) {
            if (!tile.geometry) {
                continue;
            }
            const geometry = tile.geometry;
            const baseX = Math.round(
                (meta.x0 + (tile.x * meta.width) / tileCount) * 10,
            );
            const baseY = Math.round(
                (meta.y0 + (tile.y * meta.height) / tileCount) * 10,
            );
            let deltaOffset = 0;
            for (let arrow = 0; arrow < geometry.count; arrow++) {
                const segmentCount = geometry.segments[arrow];
                const firstDelta = deltaOffset;
                deltaOffset += segmentCount * 6;
                const speedClass = geometry.attr[arrow] & 15;
                if (speedClass < minimum) {
                    continue;
                }
                const startX = geometry.rx[arrow] + baseX;
                const startY = geometry.ry[arrow] + baseY;
                const key = `${startX},${startY}`;
                if (seen.has(key)) {
                    continue;
                }
                seen.add(key);
                const directionClass = (geometry.attr[arrow] >> 4) & 3;
                if (directionClass >= styles.length) {
                    continue;
                }
                let px = startX / 10;
                let py = startY / 10;
                let c2x = px;
                let c2y = py;
                let endX = px;
                let endY = py;
                const styleIndex = directionClass * 11 + speedClass;
                const shaft = [`M${this.coord(px)},${this.coord(py)}`];
                for (let segment = 0; segment < segmentCount; segment++) {
                    const offset = firstDelta + segment * 6;
                    const c1x = px + geometry.deltas[offset] / 10;
                    const c1y = py + geometry.deltas[offset + 1] / 10;
                    c2x = px + geometry.deltas[offset + 2] / 10;
                    c2y = py + geometry.deltas[offset + 3] / 10;
                    endX = px + geometry.deltas[offset + 4] / 10;
                    endY = py + geometry.deltas[offset + 5] / 10;
                    shaft.push(
                        `C${this.coord(c1x)},${this.coord(c1y)} ${this.coord(c2x)},${this.coord(c2y)} ${this.coord(endX)},${this.coord(endY)}`,
                    );
                    px = endX;
                    py = endY;
                }
                shafts[styleIndex].push(shaft.join(""));
                heads[styleIndex].push(
                    this.markerPath(
                        endX,
                        endY,
                        endX - c2x,
                        endY - c2y,
                        markerScale[speedClass],
                    ),
                );
            }
        }
        const fragment = document.createDocumentFragment();
        for (let index = 0; index < shafts.length; index++) {
            if (shafts[index].length === 0) {
                continue;
            }
            const direction = Math.floor(index / 11);
            const speed = index % 11;
            const style = styles[direction][speed];
            const shaft = document.createElementNS(SVG_NS, "path");
            shaft.setAttribute("d", shafts[index].join(""));
            shaft.setAttribute("fill", "none");
            shaft.setAttribute("stroke", style.color);
            shaft.setAttribute("stroke-opacity", String(style.alpha));
            shaft.setAttribute("stroke-width", String(lineWidth));
            shaft.setAttribute("stroke-linecap", "butt");
            shaft.setAttribute("stroke-linejoin", "round");
            fragment.appendChild(shaft);

            const head = document.createElementNS(SVG_NS, "path");
            head.setAttribute("d", heads[index].join(""));
            head.setAttribute("fill", style.color);
            head.setAttribute("fill-opacity", String(style.alpha));
            fragment.appendChild(head);
        }
        this.svgGroup.replaceChildren(fragment);
    }
    private lineWidth(level: number) {
        // 地図座標1単位の実距離は日本域0.5km、GSA 1km、GSL 2km。
        // 旧線幅式を座標スケールで補正し、同じ設定値の画面上の太さを揃える。
        const model = this.shadeSelector.getParams().model;
        const coordinateScale =
            getMapMeta(model).kmPerPx / getMapMeta("GSM").kmPerPx;
        return (
            this.strokeWidth.value * (10 / 2 ** (level - 3)) * coordinateScale
        );
    }
    private styles() {
        const result = [...Array(3)].map(() =>
            [...Array(11)].map(() => ({ color: "#ffffff", alpha: 1 })),
        );
        if (this.colorMode.value === "M") {
            const style = this.colorStyle(this.singleColor.getColor());
            return result.map((row) => row.map(() => style));
        }
        if (this.colorMode.value === "D") {
            return result.map((row, direction) =>
                row.map(() => ({
                    color: ["#3333ff", "#ffff66", "#ff3333"][direction],
                    alpha: 1,
                })),
            );
        }
        const colors = this.classColors.map((picker) =>
            this.colorStyle(picker.getColor()),
        );
        return result.map((row) => row.map((_, index) => colors[index]));
    }
    private colorStyle(color: string) {
        const normalized =
            color.length === 9 ? color : `${color.slice(0, 7)}ff`;
        return {
            color: normalized.slice(0, 7),
            alpha: Number.parseInt(normalized.slice(7, 9), 16) / 255,
        };
    }
    private coord(value: number) {
        return String(Math.round(value * 10) / 10);
    }
    private markerPath(
        x: number,
        y: number,
        dx: number,
        dy: number,
        size: number,
    ) {
        if (dx === 0 && dy === 0) {
            dx = 1;
        }
        const length = Math.hypot(dx, dy);
        const cos = dx / length;
        const sin = dy / length;
        const p1x = x + (-5 * cos + 5 * sin) * size;
        const p1y = y + (-5 * sin - 5 * cos) * size;
        const p2x = x + (-5 * cos - 5 * sin) * size;
        const p2y = y + (-5 * sin + 5 * cos) * size;
        const p3x = x + 5 * cos * size;
        const p3y = y + 5 * sin * size;
        return `M${this.coord(p1x)},${this.coord(p1y)}L${this.coord(x)},${this.coord(y)}L${this.coord(p2x)},${this.coord(p2y)}L${this.coord(p3x)},${this.coord(p3y)}Z`;
    }
    private clear() {
        this.svgGroup.replaceChildren();
        this.legend.textContent = "";
    }
    private validChoice(key: string, choices: string[], fallback: string) {
        const value = this.params.get(key);
        return value && choices.includes(value) ? value : fallback;
    }
    private numberParam(
        key: string,
        fallback: number,
        minimum: number,
        maximum: number,
    ) {
        if (!this.params.has(key)) {
            return fallback;
        }
        const value = Number(this.params.get(key));
        return Number.isFinite(value)
            ? Math.max(minimum, Math.min(maximum, value))
            : fallback;
    }
}

function svgElement(name: string, attributes: Record<string, string | number>) {
    const element = document.createElementNS(SVG_NS, name);
    for (const [key, value] of Object.entries(attributes)) {
        element.setAttribute(key, String(value));
    }
    return element;
}

function svgText(
    x: number,
    y: number,
    content: string,
    fontSize: number,
    anchor = "middle",
) {
    const element = svgElement("text", {
        x,
        y,
        "font-size": fontSize,
        fill: "#000",
        "text-anchor": anchor,
    });
    element.textContent = content;
    return element as SVGTextElement;
}
